import React, { useState, useEffect } from 'react'
import {
  Table,
  Input,
  Button,
  Space,
  Card,
  Collapse,
  DatePicker,
  Row,
  Col,
  message,
  Empty,
  Spin,
  Drawer,
  Descriptions,
  Divider,
  Tag
} from 'antd'
import {
  SearchOutlined,
  ExportOutlined,
  PrinterOutlined,
  DownOutlined,
  UpOutlined,
  EyeOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/AuthContext'
import api from '../utils/api'
import dayjs from 'dayjs'
import { useMediaQuery } from 'react-responsive'
import {
  MobileRecordList,
  MobileRecordCard,
  MobileCardPagination
} from '../components/common/MobileRecordCard'
import { now as tzNow } from '../utils/timezone'

const { RangePicker } = DatePicker
const { Panel } = Collapse

const PageContainer = styled.div`
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`

const ActionButtons = styled(Space)`
  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
  }
`

const StyledButton = styled(Button)`
  border-radius: 8px;
  font-weight: 500;
  height: 40px;
  padding: 0 20px;

  @media (max-width: 768px) {
    height: 36px;
    padding: 0 16px;
    font-size: 14px;
  }
`

const PrimaryButton = styled(StyledButton)`
  background: #7c3aed;
  border-color: #7c3aed;
  color: #fff;

  &:hover {
    background: #6d28d9;
    border-color: #6d28d9;
  }

  &.active {
    background: #6d28d9;
    border-color: #6d28d9;
  }
`

const SecondaryButton = styled(StyledButton)`
  background: #fff;
  border-color: #7c3aed;
  color: #7c3aed;

  &:hover {
    background: #f3f0ff;
    border-color: #6d28d9;
    color: #6d28d9;
  }
`

const SearchCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
  border: none;

  .ant-card-body {
    padding: 24px;

    @media (max-width: 768px) {
      padding: 16px;
    }
  }

  .ant-collapse {
    background: transparent;
    border: none;

    .ant-collapse-item {
      border: none;

      .ant-collapse-header {
        padding: 0 !important;
        font-size: 16px;
        font-weight: 600;
        color: #1a1a1a;

        .ant-collapse-arrow {
          right: 0;
          padding: 0;
        }
      }

      .ant-collapse-content {
        border: none;
        background: transparent;
        padding-top: 16px;
      }
    }
  }
`

const SearchForm = styled.div`
  .search-row {
    margin-bottom: 16px;

    @media (max-width: 768px) {
      margin-bottom: 12px;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  .search-label {
    display: block;
    margin-bottom: 8px;
    color: #374151;
    font-weight: 500;
    font-size: 14px;
  }

  .ant-picker {
    width: 100%;
    border-radius: 8px;
    border: 1px solid #e5e7eb;

    &:hover,
    &:focus {
      border-color: #7c3aed;
    }

    &.ant-picker-focused {
      border-color: #7c3aed;
      box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.1);
    }
  }
`

const SummaryCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
  border: none;
  background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);

  .ant-card-body {
    padding: 24px;

    @media (max-width: 768px) {
      padding: 16px;
    }
  }

  .summary-title {
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  .summary-value {
    color: #fff;
    font-size: 32px;
    font-weight: 700;

    @media (max-width: 768px) {
      font-size: 24px;
    }
  }
`

const TableCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: none;

  .ant-card-body {
    padding: 0;
  }

  .ant-table {
    .ant-table-thead > tr > th {
      background: #f9fafb;
      border-bottom: 2px solid #e5e7eb;
      font-weight: 600;
      color: #374151;
      padding: 16px;
      text-align: center;

      @media (max-width: 768px) {
        padding: 12px 8px;
        font-size: 12px;
      }
    }

    .ant-table-tbody > tr > td {
      padding: 16px;
      text-align: center;
      border-bottom: 1px solid #f3f4f6;

      @media (max-width: 768px) {
        padding: 12px 8px;
        font-size: 12px;
      }
    }

    .ant-table-tbody > tr:hover > td {
      background: #f9fafb;
    }
  }
`

const ValueAddedConsumptionPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const [loading, setLoading] = useState(false)
  const [dateRange, setDateRange] = useState([
    tzNow().subtract(30, 'day'),
    tzNow()
  ])
  const [records, setRecords] = useState([])
  const [totalConsumed, setTotalConsumed] = useState(0)
  // 查看細節 Drawer
  const [detailVisible, setDetailVisible] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailOrder, setDetailOrder] = useState(null)
  const [detailRecord, setDetailRecord] = useState(null)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })
  const [searchExpanded, setSearchExpanded] = useState(true)

  useEffect(() => {
    fetchRecords()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize])

  const fetchRecords = async () => {
    setLoading(true)
    try {
      const response = await api.get(
        `/api/businesses/${user.businessId}/value-added-consumption`,
        {
          params: {
            page: pagination.current,
            pageSize: pagination.pageSize,
            startDate: dateRange[0]?.format('YYYY-MM-DD'),
            endDate: dateRange[1]?.format('YYYY-MM-DD')
          }
        }
      )

      const payload = response.data?.data || response.data || {}
      setRecords(payload.records || [])
      setTotalConsumed(payload.totalConsumed || 0)
      setPagination({
        ...pagination,
        total: payload.total || 0
      })
    } catch (error) {
      console.error('獲取消耗記錄失敗:', error)
      setRecords([])
      setTotalConsumed(0)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 })
    fetchRecords()
  }

  const handleTableChange = (paginationConfig) => {
    setPagination({
      ...pagination,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    })
  }

  // 查看細節：開啟 Drawer 並抓取該筆結帳訂單完整內容
  const handleViewDetail = async (record) => {
    setDetailRecord(record)
    setDetailOrder(null)
    setDetailVisible(true)
    const orderKey = record._id || record.orderId
    if (!orderKey) return
    setDetailLoading(true)
    try {
      const response = await api.get(
        `/api/businesses/${user.businessId}/checkout/orders/${orderKey}`
      )
      const order = response.data?.data || response.data || null
      setDetailOrder(order)
    } catch (error) {
      console.error('獲取訂單詳情失敗:', error)
      message.error(
        error.response?.data?.message || '獲取訂單詳情失敗，請稍後再試'
      )
    } finally {
      setDetailLoading(false)
    }
  }

  const columns = [
    {
      title: '項次',
      dataIndex: 'index',
      key: 'index',
      width: 80,
      render: (_, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1
    },
    {
      title: '客戶姓名',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (text) => text || '-'
    },
    {
      title: '客戶電話',
      dataIndex: 'customerPhone',
      key: 'customerPhone',
      render: (text) => text || '-'
    },
    {
      title: '使用日期',
      dataIndex: 'usageDate',
      key: 'usageDate',
      render: (date) => (date ? dayjs(date).format('YYYY-MM-DD') : '-')
    },
    {
      title: '使用金額',
      dataIndex: 'usageAmount',
      key: 'usageAmount',
      align: 'right',
      render: (amount) => (amount ? `NT$ ${amount.toLocaleString()}` : '-')
    },
    {
      title: '功能',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type='link'
          size='small'
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          細節
        </Button>
      )
    }
  ]

  // 手機版：表格轉卡片
  const renderMobileCards = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      )
    }
    if (!records || records.length === 0) {
      return (
        <div style={{ padding: 24 }}>
          <Empty description='暫無消耗紀錄' />
        </div>
      )
    }
    return (
      <>
        <MobileRecordList $padded>
          {records.map((record, index) => {
            const seq =
              (pagination.current - 1) * pagination.pageSize + index + 1
            return (
              <MobileRecordCard
                key={record._id}
                title={record.customerName || '-'}
                subtitle={`#${seq}`}
                extra={
                  <Button
                    size='small'
                    icon={<EyeOutlined />}
                    onClick={() => handleViewDetail(record)}
                  >
                    細節
                  </Button>
                }
                rows={[
                  {
                    label: '客戶電話',
                    value: record.customerPhone,
                    hidden: !record.customerPhone
                  },
                  {
                    label: '使用日期',
                    value: record.usageDate
                      ? dayjs(record.usageDate).format('YYYY-MM-DD')
                      : '-'
                  },
                  {
                    label: '使用金額',
                    value: record.usageAmount
                      ? `NT$ ${record.usageAmount.toLocaleString()}`
                      : '-'
                  }
                ]}
              />
            )
          })}
        </MobileRecordList>
        <MobileCardPagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onChange={(page, pageSize) =>
            handleTableChange({ current: page, pageSize })
          }
        />
      </>
    )
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>儲值消費紀錄</PageTitle>
        <ActionButtons>
          <PrimaryButton
            active
            onClick={() => navigate('/value-added/records')}
          >
            儲值記錄查詢
          </PrimaryButton>
          <PrimaryButton
            onClick={() => navigate('/value-added/remaining-balance')}
          >
            剩餘儲值金查詢
          </PrimaryButton>
        </ActionButtons>
      </PageHeader>

      <SearchCard>
        <Collapse
          activeKey={searchExpanded ? ['search'] : []}
          onChange={(keys) => setSearchExpanded(keys.includes('search'))}
          expandIcon={({ isActive }) =>
            isActive ? <UpOutlined /> : <DownOutlined />
          }
        >
          <Panel
            header={
              <span>
                <SearchOutlined style={{ marginRight: 8 }} />
                搜尋
              </span>
            }
            key='search'
          >
            <SearchForm>
              <div className='search-row'>
                <label className='search-label'>日期</label>
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates)}
                  format='YYYY-MM-DD'
                  style={{ width: '100%' }}
                  size='large'
                  allowClear
                  inputReadOnly={isMobile}
                  getPopupContainer={(trigger) => trigger.parentElement}
                />
              </div>
              <div style={{ textAlign: 'right', marginTop: 16 }}>
                <PrimaryButton icon={<SearchOutlined />} onClick={handleSearch}>
                  搜尋
                </PrimaryButton>
              </div>
            </SearchForm>
          </Panel>
        </Collapse>
      </SearchCard>

      <SummaryCard>
        <div className='summary-title'>消耗儲值金</div>
        <div className='summary-value'>
          NT$ {totalConsumed.toLocaleString()}
        </div>
      </SummaryCard>

      <TableCard>
        {isMobile ? (
          renderMobileCards()
        ) : (
          <Table
            columns={columns}
            dataSource={records}
            rowKey='_id'
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 筆記錄`,
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            onChange={handleTableChange}
            scroll={{ x: 800 }}
          />
        )}
      </TableCard>

      <Drawer
        title='儲值消費細節'
        placement='right'
        width={isMobile ? '100%' : 460}
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
        destroyOnClose
      >
        <Spin spinning={detailLoading}>
          {(() => {
            const rec = detailRecord || {}
            const order = detailOrder || {}
            const payment = order.payment || {}
            const services = order.services || []
            const products = order.products || []
            const amounts = order.amounts || {}
            const money = (v) =>
              v == null ? '-' : `NT$ ${Number(v).toLocaleString()}`
            // 付款方式逐項（只顯示 > 0）
            const payRows = [
              { label: '使用儲值金', value: payment.storedValue, highlight: true },
              { label: '現金', value: payment.cash },
              { label: '轉帳', value: payment.transfer },
              { label: '刷卡', value: payment.cardPayment },
              { label: '行動支付', value: payment.mobilePayment },
              { label: '禮券/抵用', value: payment.voucher }
            ].filter((r) => Number(r.value) > 0)

            return (
              <>
                <Descriptions
                  column={1}
                  size='small'
                  labelStyle={{ width: 96, color: '#6b7280' }}
                >
                  <Descriptions.Item label='客戶'>
                    {rec.customerName || order.customer?.name || '-'}
                    {rec.customerPhone || order.customer?.phone
                      ? `（${rec.customerPhone || order.customer?.phone}）`
                      : ''}
                  </Descriptions.Item>
                  <Descriptions.Item label='銷售日期'>
                    {rec.usageDate
                      ? dayjs(rec.usageDate).format('YYYY-MM-DD') +
                        (rec.usageTime ? ` ${rec.usageTime}` : '')
                      : order.time?.date || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label='服務人員'>
                    {rec.servicePersonnel ||
                      order.customer?.servicePersonnel ||
                      '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label='訂單編號'>
                    <span style={{ fontFamily: 'monospace' }}>
                      {order.orderId || rec.orderId || '-'}
                    </span>
                  </Descriptions.Item>
                  {order.status ? (
                    <Descriptions.Item label='訂單狀態'>
                      {order.status === 'voided' ? (
                        <Tag color='default'>已作廢</Tag>
                      ) : order.status === 'paid' ? (
                        <Tag color='green'>已付款</Tag>
                      ) : (
                        <Tag>{String(order.status)}</Tag>
                      )}
                    </Descriptions.Item>
                  ) : null}
                </Descriptions>

                {services.length > 0 && (
                  <>
                    <Divider orientation='left' style={{ margin: '16px 0 8px' }}>
                      服務項目
                    </Divider>
                    {services.map((s, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '4px 0',
                          fontSize: 13
                        }}
                      >
                        <span>
                          {s.serviceName || '服務'}
                          {s.quantity > 1 ? ` ×${s.quantity}` : ''}
                        </span>
                        <span>{money((s.price || 0) * (s.quantity || 1))}</span>
                      </div>
                    ))}
                  </>
                )}

                {products.length > 0 && (
                  <>
                    <Divider orientation='left' style={{ margin: '16px 0 8px' }}>
                      商品項目
                    </Divider>
                    {products.map((p, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '4px 0',
                          fontSize: 13
                        }}
                      >
                        <span>
                          {p.productName || p.name || '商品'}
                          {p.quantity > 1 ? ` ×${p.quantity}` : ''}
                        </span>
                        <span>{money((p.price || 0) * (p.quantity || 1))}</span>
                      </div>
                    ))}
                  </>
                )}

                <Divider orientation='left' style={{ margin: '16px 0 8px' }}>
                  付款明細
                </Divider>
                {payRows.length === 0 ? (
                  <div style={{ color: '#999', fontSize: 13 }}>—</div>
                ) : (
                  payRows.map((r, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '4px 0',
                        fontSize: 13,
                        fontWeight: r.highlight ? 600 : 400,
                        color: r.highlight ? '#7c3aed' : '#333'
                      }}
                    >
                      <span>{r.label}</span>
                      <span>{money(r.value)}</span>
                    </div>
                  ))
                )}

                <Divider style={{ margin: '12px 0' }} />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 13,
                    color: '#6b7280',
                    padding: '2px 0'
                  }}
                >
                  <span>訂單應收</span>
                  <span>{money(amounts.receivable ?? order.payment?.receivable)}</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 15,
                    fontWeight: 700,
                    color: '#7c3aed',
                    padding: '2px 0'
                  }}
                >
                  <span>本單使用儲值金</span>
                  <span>{money(rec.usageAmount ?? payment.storedValue)}</span>
                </div>
              </>
            )
          })()}
        </Spin>
      </Drawer>
    </PageContainer>
  )
}

export default ValueAddedConsumptionPage
